// AcceptanceMailHistory — CU3/CU5: the acceptance form's send history. Story #73 (#2795) D12 adds
// a per-attempt download action (S20): each dispatch offers downloading the exact binaries that were
// sent in that attempt (FORM or SIGNED), read-only, in any acceptance state. An empty history is a
// normal, non-error state (S21). Rendering order follows whatever the backend returns (RN10:
// sorting descending by date is Sun's responsibility, not re-derived here).
//
// RN2 — an attempt carries N files (a SIGNED send attaches the signed PDF plus the evidences
// uploaded with it) and archives all of them, so each row reaches every attached file instead of a
// single one that could only ever return the signed PDF. A single attachment downloads straight from
// the row; several open a dialog listing them, because a send with a dozen evidences stretched the
// row into an unreadable column of links.
import { useEffect, useState } from 'react'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem,
  ListItemText, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from '@mui/material'
import { AttachFileRounded, DownloadRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { apiGet } from '../generic/Api_Functions'
import { downloadAcceptanceDocument } from './acceptanceDocumentContent'

const DOCUMENT_TYPE_LABEL_KEYS = {
  FORM: 'Acceptance form',
  SIGNED: 'Signed document'
}

// One archived file of one attempt. `attachments` is absent on a response from a backend older than
// this change, in which case the pre-existing single-attachment route is still the way to reach it.
const attachmentsOf = (dispatch) => (Array.isArray(dispatch.attachments) ? dispatch.attachments : [])

const attachmentContentPath = (fabricationOrderId, dispatchId, attachmentId) =>
  `fabricationOrderAcceptance/${fabricationOrderId}/mail-history/${dispatchId}/attachments/${attachmentId}/content`

const legacyAttachmentContentPath = (fabricationOrderId, dispatchId) =>
  `fabricationOrderAcceptance/${fabricationOrderId}/mail-history/${dispatchId}/attachment`

const KILOBYTE = 1024
// Symbols, not words: conventional across every language this app ships, so they need no translation
// (and no eight new keys per unit).
const SIZE_UNITS = ['B', 'KB', 'MB', 'GB']

const formatSizeBytes = (sizeBytes) => {
  let value = Number(sizeBytes) || 0
  let unit = 0
  while (value >= KILOBYTE && unit < SIZE_UNITS.length - 1) {
    value = value / KILOBYTE
    unit += 1
  }
  // Bytes are whole; anything scaled reads better with one decimal.
  return `${unit === 0 ? value : value.toFixed(1)} ${SIZE_UNITS[unit]}`
}

export default function AcceptanceMailHistory ({ fabricationOrderId, refreshToken = 0 }) {
  const { t_ } = useSmartTranslation()
  const [dispatches, setDispatches] = useState([])
  // The attempt whose attachments are listed in the dialog; {} = closed.
  const [listing, setListing] = useState({})

  useEffect(() => {
    apiGet(`fabricationOrderAcceptance/${fabricationOrderId}/mail-history`)
      .then((result) => setDispatches(Array.isArray(result) ? result : []))
      .catch(() => setDispatches([]))
  }, [fabricationOrderId, refreshToken])

  const download = (dispatchId, attachment) => downloadAcceptanceDocument(
    attachmentContentPath(fabricationOrderId, dispatchId, attachment.id),
    attachment.originalFilename
  )

  const renderActions = (dispatch) => {
    const attachments = attachmentsOf(dispatch)

    // Older backend: no attachment metadata, so the single-attachment route stays the only way in.
    if (attachments.length === 0) {
      return (
        <Button
          size='small' startIcon={<DownloadRounded />}
          onClick={() => downloadAcceptanceDocument(
            legacyAttachmentContentPath(fabricationOrderId, dispatch.id),
            `mail-attachment-${dispatch.id}.pdf`
          )}
        >
          {t_('Download')}
        </Button>
      )
    }

    // A dialog for one file would be pure friction: download it straight from the row.
    if (attachments.length === 1) {
      return (
        <Button
          size='small' startIcon={<DownloadRounded />}
          onClick={() => download(dispatch.id, attachments[0])}
        >
          {attachments[0].originalFilename}
        </Button>
      )
    }

    return (
      <Button
        size='small' startIcon={<AttachFileRounded />}
        onClick={() => setListing(dispatch)}
      >
        {`${t_('Attachments')} (${attachments.length})`}
      </Button>
    )
  }

  return (
    <Box data-testid='acceptance-mail-history' sx={{ mt: 1.5 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
        {t_('Send history')}
      </Typography>
      {dispatches.length === 0
        ? (
          <Typography variant='body2' color='text.secondary'>
            {t_('No emails have been sent yet.')}
          </Typography>
          )
        : (
          <Paper variant='outlined'>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Date/time')}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Sender')}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Recipients')}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Subject')}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Document type')}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{t_('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dispatches.map((dispatch, index) => (
                  <TableRow key={dispatch.id != null ? dispatch.id : index}>
                    <TableCell>{dispatch.sentAt}</TableCell>
                    <TableCell>{dispatch.sender}</TableCell>
                    <TableCell>{(dispatch.recipients || []).join(', ')}</TableCell>
                    <TableCell>{dispatch.subject}</TableCell>
                    <TableCell>{t_(DOCUMENT_TYPE_LABEL_KEYS[dispatch.documentType] || dispatch.documentType)}</TableCell>
                    <TableCell>{renderActions(dispatch)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          )}

      {/* RN2 — every file archived for the attempt, each downloadable exactly as it was sent. */}
      <Dialog open={Boolean(listing.id)} onClose={() => setListing({})} fullWidth maxWidth='sm'>
        <DialogTitle>{t_('Attachments')}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List data-testid='mail-history-attachments' dense disablePadding>
            {attachmentsOf(listing).map((attachment) => (
              <ListItem
                key={attachment.id}
                secondaryAction={
                  <IconButton
                    edge='end' aria-label={`${t_('Download')} ${attachment.originalFilename}`}
                    onClick={() => download(listing.id, attachment)}
                  >
                    <DownloadRounded />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={attachment.originalFilename}
                  secondary={formatSizeBytes(attachment.sizeBytes)}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListing({})}>{t_('Close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
